import role14MfrsSchema from '../../../../assets/forms/role-320000-mfrs.form.json';
import role14MpersSchema from '../../../../assets/forms/role-320000-mpers.form.json';
import { SSMT_SCOPE } from '../../../constants/qnames';

/**
 * Shared business rules for Role 14 (Statement of Profit or Loss — By Nature).
 *
 * Role 14 is the alternative SOPL layout (classification by NATURE of expense
 * vs. role 12's classification by FUNCTION). It is accounting-standard
 * dependent: the same logical sheet ships as two sibling form JSONs —
 * role-320000-mfrs.form.json (qname prefixes `ifrs-full:*` / `ssmt:*`) and
 * role-320000-mpers.form.json (qname prefixes `ifrs-smes:*` / `ssmt-mpers:*`).
 *
 * Both role14-320000-statement-of-profit-or-loss-by-nature.component.ts AND
 * role14SoplByNatureValidator import from this file so the schema selection
 * literal and shared types live in exactly one place. Mirrors role 13's
 * picker semantics (MFRS = exact match; MPERS = fallback) since both sheets
 * read their accounting standard via `CrossReferenceService.isMFRS()` —
 * **opposite** of role 20's MFRS-default picker. Do not "normalise" the
 * inversion without auditing the component.
 *
 * ---- What gets validated --------------------------------------------------
 *
 * Role 14's component publishes its sheet-level status via exactly ONE site:
 *
 *   this.stateService.markSheetInitializedWithStatus(
 *     'statementOfProfitOrLossByNature', false
 *   );
 *
 * i.e. `hasErrors` is hard-coded to `false` at init time and never re-published
 * thereafter. There is no `evaluateSheetStatus()` and no `updateSheetLevelStatus`
 * call anywhere downstream of valueChanges. Therefore the parity-relevant
 * surface is structurally a no-op today — the pure validator returns
 * `hasErrors: false` deterministically.
 *
 * The component DOES run two side-effect-only flows on valueChanges:
 *
 *   • `calculateTotals()` — populates the four calculated rows
 *     (ProfitFromOperationsByNature, ProfitBeforeTaxByNature,
 *     ProfitForTheYearByNature, TotalProfitAttributionByNature) by row-summing
 *     across (Revenue + OtherIncome ± each expense by nature) for each visible
 *     column suffix.
 *   • `validateAgainstApiData()` — compares calculated rows against an API
 *     baseline persisted in `localStorage`. Mismatches drive the per-row
 *     `Valid*` badge (suggested change), NOT `hasErrors`.
 *
 * Neither of these influences the published `SheetLevelStatusData`, so the
 * pure validator deliberately mirrors the no-op contract. See the "Calculation qname
 * mismatch" note in `role14-sopl-by-nature.validator.ts` for the latent calculation
 * bug we surfaced while reading the component end-to-end.
 *
 * ---- Cross-sheet dependency surface --------------------------------------
 *
 * Per `SheetDependencyService`, role 14 is downstream of role 2
 * (`scopeOfFiling`) and role 0 (`filingPrerequisites`, central config hub).
 * The component reads:
 *
 *   • `scopeOfFiling` — `DisclosureOfWhetherFinancialStatementsAreConsolidated`
 *     and `DisclosureOfComparativeInformation` (drives column scenario:
 *     1 / 2 / 4 visible columns).
 *   • `filingPrerequisites` — accounting-standard pick (MFRS vs MPERS).
 *
 * From the validator's perspective these only influence column visibility,
 * which only matters when row-level data validation runs — and role 14 has
 * no such validation today. The validator still reads
 * `context.crossSheetData` defensively so a future schema edit (e.g. adding a
 * `requiredIf scopeOfFiling.X equals Y` rule) plugs in without re-threading
 * the interface.
 */

/**
 * Literal value of
 * filingInfo['ssmt-dei:BasisOfAccountingStandardsAppliedToPrepareFinancialStatements']
 * that selects the MFRS schema. Mirrors `CrossReferenceService.isMFRS()` —
 * exact match required, MPERS is the implicit fallback for any other value.
 */
export const ROLE14_MFRS_ACCOUNTING_STANDARD =
  'Malaysian Financial Reporting Standards';

/** Filename of the MFRS variant schema. */
export const ROLE14_MFRS_SCHEMA_FILE = 'role-320000-mfrs.form.json';

/** Filename of the MPERS (default / fallback) variant schema. */
export const ROLE14_MPERS_SCHEMA_FILE = 'role-320000-mpers.form.json';

/**
 * scopeOfFiling field that drives the consolidation column scenario. Mirrors
 * the component's `checkConsolidatedStatus()` literal.
 */
export const ROLE14_CONSOLIDATED_FIELD =
  SSMT_SCOPE.DisclosureOfWhetherFinancialStatementsAreConsolidated;
export const ROLE14_CONSOLIDATED_VALUE = 'Yes';

/**
 * scopeOfFiling field that decides whether previous-year columns are visible.
 * Both 'Comparative information presented' and 'Restated comparative
 * information' produce a previous-year column; anything else (including
 * undefined) hides it. Mirrors the component's `checkConsolidatedStatus()`.
 */
export const ROLE14_COMPARATIVE_INFO_FIELD =
  SSMT_SCOPE.DisclosureOfComparativeInformation;
export const ROLE14_COMPARATIVE_INFO_PRESENT_VALUES: ReadonlyArray<string> = [
  'Comparative information presented',
  'Restated comparative information',
];

/**
 * Narrow shape of a role 14 schema control used by the pure validator. The
 * schemas carry richer metadata (ui.level, currency, placeholder, dualColumn,
 * decimalPlaces, etc.) that drives the editor's renderer but does not
 * influence sheet-level validation today.
 */
export interface Role14SchemaControl {
  qname: string;
  label?: string;
  type?: string;
  required?: boolean;
  validators?: Array<{ name?: string; conditionalOn?: unknown }>;
  ui?: {
    control?: string;
    controlType?: string;
    dualColumn?: boolean;
    calculated?: boolean;
    readonly?: boolean;
    level?: number;
  };
}

export interface Role14Schema {
  role: string;
  sheetId: string;
  label: string;
  description?: string;
  controls: Role14SchemaControl[];
}

/**
 * Resolve the correct role 14 schema filename for the given accounting
 * standard. Mirrors `CrossReferenceService.isMFRS()` so the component's
 * fetch() and the validator's static import both derive from the same rule.
 *
 * Default = MPERS (fallback for anything that isn't the exact MFRS literal).
 * This INVERTS role 20's MFRS-default picker — see the file header.
 */
export function pickRole14SchemaFile(
  accountingStandard: string | undefined | null
): string {
  if (accountingStandard === ROLE14_MFRS_ACCOUNTING_STANDARD) {
    return ROLE14_MFRS_SCHEMA_FILE;
  }
  return ROLE14_MPERS_SCHEMA_FILE;
}

/**
 * Resolve the actual role 14 schema object for the given accounting standard.
 * Used by the pure validator which cannot fetch() at validate-time (the
 * listing/dashboard cache-miss path runs synchronously) so both variants are
 * imported eagerly and selected from memory.
 */
export function pickRole14Schema(
  accountingStandard: string | undefined | null
): Role14Schema {
  if (accountingStandard === ROLE14_MFRS_ACCOUNTING_STANDARD) {
    return role14MfrsSchema as unknown as Role14Schema;
  }
  return role14MpersSchema as unknown as Role14Schema;
}

/**
 * Schema controls that participate in validation. Excludes section headers
 * exactly as the component's buildForm() does (`ui.control === 'header'` is
 * the editor's skip-list — it filters rows and never creates a FormControl
 * for them). Section headers therefore cannot fail validation.
 */
export function getRole14ValidatingControls(
  schema: Role14Schema
): ReadonlyArray<Role14SchemaControl> {
  return schema.controls.filter(
    (control) =>
      control.type !== 'SectionHeader' &&
      control.ui?.control !== 'header' &&
      control.ui?.controlType !== 'section-header'
  );
}

/**
 * Is this control a calculated / readonly total? Calculated fields are
 * populated by the component's `calculateTotals()` after valueChanges settle;
 * the editor never marks them required. If a future schema edit ever sets
 * `required: true` on a calculated row, this skip preserves the editor's
 * "calculation engine fills it" behaviour. Mirrors role 13's helper.
 */
export function isRole14CalculatedControl(
  control: Role14SchemaControl
): boolean {
  return control.ui?.calculated === true || control.ui?.readonly === true;
}

/**
 * The four column-suffix permutations the component's buildForm() creates
 * for a `dualColumn` monetary row. Order matches the component's
 * `setDemoValues()` / `calculateTotals()` so future readers can audit the
 * mapping at a glance.
 */
export type Role14ColumnSuffix =
  | 'GroupCurrent'
  | 'GroupPrevious'
  | 'CompanyCurrent'
  | 'CompanyPrevious'
  | 'Current'
  | 'Previous';

/**
 * Resolve the visible column suffixes for a given scopeOfFiling slice.
 * Mirrors the component's `checkConsolidatedStatus()` ladder:
 *
 *   - Consolidated + previous year → 4 cols (Group/Company × Current/Previous)
 *   - Consolidated only            → 2 cols (GroupCurrent, CompanyCurrent)
 *   - Previous year only           → 2 cols (Current, Previous)
 *   - Neither                      → 1 col  (Current)
 *
 * When `scopeOfFiling` is absent (cross-sheet bundle missing or mid-init)
 * the component's pre-init defaults are `isConsolidated = false`,
 * `hasPreviousYear = false` — i.e. the single-`Current`-column scenario.
 * The validator follows the same default so an absent slice does not
 * spuriously expand the column set.
 */
export function resolveRole14ColumnSuffixes(
  scopeOfFiling: Record<string, unknown> | undefined
): ReadonlyArray<Role14ColumnSuffix> {
  const isConsolidated =
    scopeOfFiling?.[ROLE14_CONSOLIDATED_FIELD] === ROLE14_CONSOLIDATED_VALUE;
  const comparativeInfo = scopeOfFiling?.[ROLE14_COMPARATIVE_INFO_FIELD];
  const hasPreviousYear =
    typeof comparativeInfo === 'string' &&
    ROLE14_COMPARATIVE_INFO_PRESENT_VALUES.includes(comparativeInfo);

  if (isConsolidated && hasPreviousYear) {
    return ['GroupCurrent', 'GroupPrevious', 'CompanyCurrent', 'CompanyPrevious'];
  }
  if (isConsolidated && !hasPreviousYear) {
    return ['GroupCurrent', 'CompanyCurrent'];
  }
  if (!isConsolidated && hasPreviousYear) {
    return ['Current', 'Previous'];
  }
  return ['Current'];
}

/**
 * Empty-cell predicate for required-field checks. Mirrors `Validators.required`
 * semantics (null / undefined / '' are missing; 0 is present — monetary fields
 * legitimately accept zero).
 */
export function isRole14Empty(value: unknown): boolean {
  return value === null || value === undefined || value === '';
}
